import { Alert, PageHeader } from '@aaimplatform/ui';
import { SettingRowsSection, type SettingRowConfig } from './SettingRows';

/**
 * The settings this page names, grouped as the page presents them.
 *
 * None of them is a field of PlatformSettings or UpdatePlatformSettingsRequest
 * in schema-registry/platform-admin.openapi.yaml, and no platform service
 * reads them. The page used to seed an editable form from client literals
 * (75 / 65 / 80 percent LTV, 8.5 percent interest, $50,000 and $100,000
 * approval thresholds) and PATCH them into the settings document, so an
 * operator saw literals presented as stored configuration. Until the contract
 * declares where each value lives, every row reads "Not configured" and the
 * page sends nothing.
 */
export const DEFAULTS_SECTIONS: { title: string; rows: SettingRowConfig[] }[] = [
  {
    title: 'LTV Thresholds',
    rows: [
      { label: 'Maximum LTV', description: 'Maximum loan-to-value ratio allowed' },
      { label: 'Warning threshold', description: 'LTV level that triggers warning alerts' },
      { label: 'Critical threshold', description: 'LTV level that triggers critical alerts' },
    ],
  },
  {
    title: 'Loan Parameters',
    rows: [
      { label: 'Minimum loan term', description: 'Shortest allowed loan duration' },
      { label: 'Maximum loan term', description: 'Longest allowed loan duration' },
      { label: 'Default interest rate', description: 'Starting interest rate for new loans' },
    ],
  },
  {
    title: 'Approval Workflow',
    rows: [
      { label: 'Auto-approval', description: 'Automatically approve loans under a threshold' },
      { label: 'Auto-approval threshold', description: 'Maximum loan amount for auto-approval' },
      { label: 'Dual approval', description: 'Require two approvers for large loans' },
      { label: 'Dual approval threshold', description: 'Loan amount requiring dual approval' },
    ],
  },
  {
    title: 'Risk Parameters',
    rows: [
      { label: 'Minimum risk score', description: 'Loans below this score require manual review' },
      { label: 'Asset concentration limit', description: 'Maximum portfolio exposure to a single asset type' },
    ],
  },
];

/**
 * The page description. It names the defaults this page is meant to hold and
 * claims nothing about new tenants: nothing stores these values, so none is
 * applied to any tenant.
 */
export const DEFAULTS_DESCRIPTION =
  'The tenant defaults this page is meant to hold: LTV, loan terms, approval workflow and risk parameters.';

/**
 * System Defaults - Platform Admin
 *
 * Read-only. Lists the tenant defaults this surface is meant to hold and
 * states that none is stored.
 */
export function DefaultsConfigPage() {
  return (
    <div>
      <PageHeader description={DEFAULTS_DESCRIPTION} />

      <Alert type="warning" title="Not stored" className="mb-6">
        No platform setting stores these defaults, and no platform service
        reads them, so this page cannot show or save a value. Each setting
        reads Not configured until a stored setting holds it.
      </Alert>

      {DEFAULTS_SECTIONS.map((section) => (
        <SettingRowsSection key={section.title} title={section.title} rows={section.rows} />
      ))}
    </div>
  );
}
